import { useQuery } from "@tanstack/react-query";
import { ChevronRight } from "lucide-react";
import { Collapsible as CollapsiblePrimitive } from "radix-ui";
import { Link, useLocation } from "react-router";

import { PERMISSIONS, type Permission } from "@/api/permissions";
import { reviewQueueSummaryQuery } from "@/api/queries/reviews";
import type { ReviewQueueSummary } from "@/api/types";
import { useWhoami } from "@/auth/use-whoami";
import { QueueCountBadge } from "@/components/queue/QueueCountBadge";
import {
  isNavItemActive,
  NAV_FLATTEN_THRESHOLD,
  NAV_SECTIONS,
  type NavItem,
  type NavSection,
} from "@/lib/nav";
import {
  setSectionCollapsed,
  useAutoExpandActiveSection,
  useCollapsedSections,
} from "@/lib/nav-collapse";
import { cn } from "@/lib/utils";

const QUEUE_PERMISSIONS: Permission[] = [
  PERMISSIONS.KYC_REVIEW,
  PERMISSIONS.CARD_VERIFICATION_REVIEW,
  PERMISSIONS.KYC_VERIFY_BANK,
  PERMISSIONS.CRYPTO_DEPOSIT_READ,
  PERMISSIONS.CRYPTO_WITHDRAWAL_READ,
  // Stables RFIs
  PERMISSIONS.USERS_READ,
];

/**
 * The sidebar nav, rendered twice: in the desktop rail and in the mobile
 * drawer. Identical in both — same permissions, same groups, same collapse
 * state, which lives outside React (`lib/nav-collapse.ts`) precisely because
 * both copies can be mounted at once.
 */
export function SidebarNav({ onNavigate }: { onNavigate?: () => void }) {
  const { data: whoami } = useWhoami();
  const { pathname } = useLocation();
  const collapsed = useCollapsedSections();
  useAutoExpandActiveSection(pathname);

  // Fails closed: an item is hidden until whoami confirms the permission, and
  // a section left with nothing in it renders nothing at all — no orphan
  // header.
  const sections: NavSection[] = NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter(
      (item) => whoami?.permissions.includes(item.permission) ?? false,
    ),
  })).filter((section) => section.items.length > 0);

  // Live queue counts beside Reviews / KYC / Crypto — one tiny request a
  // minute, and only for roles that hold a queue permission (the endpoint
  // 403s others).
  const canSeeQueues =
    whoami?.permissions.some((permission) =>
      QUEUE_PERMISSIONS.includes(permission),
    ) ?? false;
  const summary = useQuery({
    ...reviewQueueSummaryQuery(),
    enabled: canSeeQueues,
  });

  // A role with only a handful of pages (marketing has two) gets no group
  // chrome at all: headers and chevrons around a two-line list are noise.
  const visibleCount = sections.reduce(
    (total, section) => total + section.items.length,
    0,
  );

  if (visibleCount <= NAV_FLATTEN_THRESHOLD) {
    return (
      <nav aria-label="Main" className="space-y-0.5 p-2">
        {sections
          .flatMap((section) => section.items)
          .map((item) => (
            <NavItemLink
              key={item.to}
              item={item}
              pathname={pathname}
              summary={summary.data}
              onNavigate={onNavigate}
            />
          ))}
      </nav>
    );
  }

  return (
    <nav aria-label="Main" className="space-y-1 p-2">
      {sections.map((section) =>
        section.label === null ? (
          <div key={section.id} className="space-y-0.5">
            {section.items.map((item) => (
              <NavItemLink
                key={item.to}
                item={item}
                pathname={pathname}
                summary={summary.data}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        ) : (
          <NavGroup
            key={section.id}
            section={section}
            pathname={pathname}
            collapsed={collapsed.includes(section.id)}
            summary={summary.data}
            onNavigate={onNavigate}
          />
        ),
      )}
    </nav>
  );
}

function NavGroup({
  section,
  pathname,
  collapsed,
  summary,
  onNavigate,
}: {
  section: NavSection;
  pathname: string;
  collapsed: boolean;
  summary: ReviewQueueSummary | undefined;
  onNavigate?: () => void;
}) {
  // Never force-open on the active route: `useAutoExpandActiveSection` has
  // already expanded this group before paint, so the header stays a live
  // toggle instead of a button that visibly does nothing.
  const open = !collapsed;
  // Pending queue work never hides behind a chevron: the counts inside a
  // collapsed group roll up onto its header.
  const pending = summary
    ? section.items.reduce(
        (total, item) => total + (item.badge?.(summary) ?? 0),
        0,
      )
    : 0;

  return (
    <CollapsiblePrimitive.Root
      open={open}
      onOpenChange={(next) => setSectionCollapsed(section.id, !next)}
      className="border-t pt-1 first:border-t-0 first:pt-0"
    >
      <CollapsiblePrimitive.Trigger
        className={cn(
          "text-muted-foreground hover:text-foreground flex w-full items-center gap-1.5 rounded-md px-3 py-1.5",
          "text-[10px] font-semibold tracking-[0.08em] uppercase transition-colors",
        )}
      >
        <ChevronRight
          aria-hidden
          className={cn("size-3 transition-transform", open && "rotate-90")}
        />
        {section.label}
        {!open && pending > 0 && (
          <QueueCountBadge count={pending} tone="warn" className="ml-auto" />
        )}
      </CollapsiblePrimitive.Trigger>
      <CollapsiblePrimitive.Content className="data-open:animate-collapsible-down data-closed:animate-collapsible-up overflow-hidden">
        <div className="space-y-0.5 pt-0.5">
          {section.items.map((item) => (
            <NavItemLink
              key={item.to}
              item={item}
              pathname={pathname}
              summary={summary}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      </CollapsiblePrimitive.Content>
    </CollapsiblePrimitive.Root>
  );
}

function NavItemLink({
  item,
  pathname,
  summary,
  onNavigate,
}: {
  item: NavItem;
  pathname: string;
  summary: ReviewQueueSummary | undefined;
  onNavigate?: () => void;
}) {
  // Deliberately <Link>, not <NavLink>: the group and the link have to agree
  // on what "active" means, and NavLink's own matching cannot know that
  // /crypto/withdrawals belongs to the Crypto entry (whose `to` is
  // /crypto/deposits) or that /users/all is not part of Users. One helper
  // decides for both.
  const active = isNavItemActive(item, pathname);
  const count = summary && item.badge ? item.badge(summary) : null;

  return (
    <Link
      to={item.to}
      onClick={onNavigate}
      aria-current={active ? "page" : undefined}
      className={cn(
        "flex items-center gap-2.5 rounded-md px-3 py-2 text-[13px] font-medium transition-colors",
        active
          ? "bg-accent text-accent-foreground"
          : "text-muted-foreground hover:bg-accent/50 hover:text-foreground",
      )}
    >
      <item.icon className="size-4" />
      {item.label}
      {count !== null && count > 0 && (
        <QueueCountBadge count={count} tone="warn" className="ml-auto" />
      )}
    </Link>
  );
}
